"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaExprLexer = exports.EXPR_GRAMMAR = void 0;
const token_1 = require("./token");
const i18n_1 = require("../../exports/i18n");
const lodash_1 = require("lodash");
exports.EXPR_GRAMMAR = [
    // The value in the record obtained by the field name constant
    { key: token_1.TokenType.Value, exp: /(\{\})|(\{(\\[{}])*[\s\S]*?[^\\]\})/ },
    // string literal
    { key: token_1.TokenType.String, exp: /(["”“](([^\\"“”])*?\\["”“])*.*?["”“])|(['‘’](([^\\'‘’])*?\\['‘’])*.*?['‘’])/ },
    // function name or field name constant
    { key: token_1.TokenType.Call, exp: /[^0-9.+\-|=*/><()（）!&%'"“”‘’^`~,，\s][^+\-|=*/><()（）!&%'"“”‘’^`~,，\s]*/ },
    // number literal
    { key: token_1.TokenType.Number, exp: /[0-9.]+/ },
    // not equal to
    { key: token_1.TokenType.NotEqual, exp: /!=/ },
    // and
    { key: token_1.TokenType.And, exp: /&&/ },
    // greater or equal to
    { key: token_1.TokenType.GreaterEqual, exp: />=/ },
    // less than or equal to
    { key: token_1.TokenType.LessEqual, exp: /<=/ },
    // or
    { key: token_1.TokenType.Or, exp: /\|\|/ },
    // comma, parameter separator
    { key: token_1.TokenType.Comma, exp: /[,，]/ },
    // Not
    { key: token_1.TokenType.Not, exp: /!/ },
    // add +
    { key: token_1.TokenType.Add, exp: /\+/ },
    // reduce -
    { key: token_1.TokenType.Minus, exp: /-/ },
    // take *
    { key: token_1.TokenType.Times, exp: /\*/ },
    // remove /
    { key: token_1.TokenType.Div, exp: /\// },
    // take remainder %
    { key: token_1.TokenType.Mod, exp: /%/ },
    // string concatenation
    { key: token_1.TokenType.Concat, exp: /&/ },
    // more than the
    { key: token_1.TokenType.Greater, exp: />/ },
    // less than
    { key: token_1.TokenType.Less, exp: /</ },
    // equal to
    { key: token_1.TokenType.Equal, exp: /=/ },
    // Left parenthesis
    { key: token_1.TokenType.LeftParen, exp: /[(（]/ },
    // closing parenthesis
    { key: token_1.TokenType.RightParen, exp: /[)）]/ },
    // whitespace characters
    { key: token_1.TokenType.Blank, exp: /\s+/ },
    // all other
    { key: token_1.TokenType.Unknown, exp: /.+/ },
];
// The lexical analyzer of the formula, according to the incoming expression, exper -> token
class FormulaExprLexer {
    constructor(expression) {
        this.errors = []; // The lexer does not throw errors directly, but temporarily stores the errors for the caller to handle
        this.expression = expression;
        this.fullMatches = this.getFullMatches();
        this.matches = this.filterUselessToken(this.fullMatches);
        this.currentIndex = -1;
    }
    get currentTokenIndex() {
        return this.currentIndex;
    }
    set currentTokenIndex(index) {
        if (!(0, lodash_1.isNumber)(index))
            return;
        this.currentIndex = index;
    }
    getNextToken() {
        this.currentIndex++;
        if (this.currentIndex > this.matches.length - 1) {
            return null;
        }
        const token = this.matches[this.currentIndex];
        return token;
    }
    getPrevToken() {
        this.currentIndex--;
        if (this.currentIndex < 0) {
            return null;
        }
        const token = this.matches[this.currentIndex];
        return token;
    }
    reset() {
        this.currentIndex = -1;
    }
    getFullMatches() {
        const matched = this.expression.match(this.pattern()) || [];
        let index = 0;
        return matched.map((str, i) => {
            const token = this.tokenizer(index, str, matched[i + 1]);
            index += token.value.length;
            return token;
        });
    }
    filterUselessToken(tokens) {
        return tokens.filter(token => {
            if (token.type === token_1.TokenType.Unknown) {
                this.errors.push(new Error((0, i18n_1.t)(i18n_1.Strings.function_err_unrecognized_operator, {
                    token: token.value,
                })));
            }
            return token.type !== token_1.TokenType.Blank && token.type !== token_1.TokenType.Unknown;
        });
    }
    tokenizer(index, str, nextStr) {
        for (let i = 0; i < exports.EXPR_GRAMMAR.length; i++) {
            const key = exports.EXPR_GRAMMAR[i].key;
            const regex = exports.EXPR_GRAMMAR[i].exp;
            const type = key;
            if (regex.test(str)) {
                // When the tokenType matches Call, it is necessary to judge whether there is a left bracket, if not,
                // it means a pureValue without curly brackets
                if (type === token_1.TokenType.Call) {
                    if (!nextStr || this.tokenizer(index, nextStr).type !== token_1.TokenType.LeftParen) {
                        return new token_1.Token(token_1.TokenType.PureValue, index, str);
                    }
                }
                return new token_1.Token(type, index, str);
            }
        }
        throw new Error(`Unexpected token: ${str}`);
    }
    pattern() {
        const pattern = exports.EXPR_GRAMMAR.map(g => `(${g.exp.source})`).join('|');
        return new RegExp(pattern, 'g');
    }
}
exports.FormulaExprLexer = FormulaExprLexer;
//# sourceMappingURL=lexer.js.map